/*
 * Created by: IDScan @ 14-12-2015
 * Last Updated by: Degree 53 @ 15-05-2019
 * Version 1.1.0
 */

// tslint:disable: array-type

export interface ICroppyOptions {
	imageSrc: string;
	magnifierCanvasId: string;
	selection: {
		coordsTextColor: string,
		highlightColor: string,
		lineColor: string,
	};
	showCornersAlways: boolean;
	targetCanvasId: string;
	padding: number;
}

interface IWorkspace {
	height: number;
	heightFactor: number;
	width: number;
	widthFactor: number;
}

const defaults: ICroppyOptions = {
	imageSrc: '',
	magnifierCanvasId: '',
	selection: {
		coordsTextColor: '#005EA5',
		highlightColor: '#f00',
		lineColor: '#000',
	},
	showCornersAlways: true,
	targetCanvasId: '',
	padding: 0,
};

export class Croppy {
	public static getFloat = (value: number) => {
		return parseFloat(value.toFixed(3));
	}

	public static getOffset = (el: HTMLElement) => {
		const rect = el.getBoundingClientRect();

		return {
			left: rect.left + document.body.scrollLeft,
			top: rect.top + document.body.scrollTop,
		};
	}

	private static normalize = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

	private static between = (value: number, min: number, max: number) => value >= min && value <= max;

	private options: ICroppyOptions;

	private $targetCanvas: HTMLCanvasElement;
	private targetCanvasContext: CanvasRenderingContext2D;
	private $magnifier: HTMLCanvasElement | null;
	private magnifierContext: CanvasRenderingContext2D | null;

	private image?: HTMLImageElement;
	private workspace: IWorkspace = {
		height: 0,
		heightFactor: 0,
		width: 0,
		widthFactor: 0,
	};
	private selectionArea!: SelectionArea;
	private rotationDegrees = 0;
	private off = 10;
	private zs = 10;
	private magSZ = this.off * this.zs;
	private mouseDragging = false;
	private isInTouchMode = false;

	private cursorDraggingIcon = 'grabbing';
	private cursorIcon = 'grab';

	constructor(options: Partial<ICroppyOptions>) {
		this.options = Object.assign({}, defaults, options);

		this.$targetCanvas = document.getElementById(this.options.targetCanvasId) as HTMLCanvasElement;
		this.targetCanvasContext = this.$targetCanvas.getContext('2d') as CanvasRenderingContext2D;
		this.$magnifier = document.getElementById(this.options.magnifierCanvasId) as HTMLCanvasElement;
		this.magnifierContext = this.$magnifier ? this.$magnifier.getContext('2d') : null;
	}

	public init = (selectionAreaPoints: Array<Array<number>>, areOriginalCoords: boolean) => {
		this.image = new Image();

		this.image.onload = () => {
			this.setWorkspace();

			selectionAreaPoints = (selectionAreaPoints && (selectionAreaPoints.length > 0) ? selectionAreaPoints : null) as any;
			this.setSelectionAreaValues(selectionAreaPoints, areOriginalCoords);

			this.drawScene();
		};

		this.image.src = this.options.imageSrc;

		this.setCursor(false);

		this.setEvents();
	}

	public crop = (elementContainerId: string) => {
		const tempCanvas = document.createElement('canvas');
		tempCanvas.width = this.$targetCanvas.width;
		tempCanvas.height = this.$targetCanvas.height;

		this.drawScene(true);

		const tCtx = tempCanvas.getContext('2d')!;
		this.drawPath(tCtx);
		tCtx.clip();
		tCtx.drawImage(this.$targetCanvas, 0, 0);
		tCtx.restore();
		this.drawScene();

		const element = document.getElementById(elementContainerId)!;
		Array.from(element.children).forEach((el) => element.removeChild(el));
		element.append(tempCanvas);
	}

	public getOriginalSelectionAreaPoints = (): { points: Array<Array<number>>, rotation: number } => {
		const points = [];
		let x;
		let y;

		for (let i = 0; i < this.selectionArea.points.length; i++) {
			x = this.selectionArea.points[i][0];
			y = this.selectionArea.points[i][1];

			x = x / this.workspace.widthFactor;
			y = y / this.workspace.heightFactor;

			x = Croppy.getFloat(x);
			y = Croppy.getFloat(y);

			//points.push([x, y]);
		}

		return {
			points,
			rotation: Croppy.getFloat(this.rotationDegrees)
		};
	}

	public getSelectionAreaPoints = () => {
		return [this.selectionArea.points, this.rotationDegrees];
	}

	public rotate = (degrees: number) => {
		this.rotationDegrees = degrees;
		this.rotateCanvas(this.targetCanvasContext);
		this.drawSelectionArea();
	}

	private setWorkspace = () => {
		if (!this.image) {
			return;
		}

		if (!this.$targetCanvas.getAttribute('width')) {
			this.workspace.width = this.image.width;
		} else {
			this.workspace.width = parseInt(this.$targetCanvas.getAttribute('width')!, 10);
		}

		if (!this.$targetCanvas.getAttribute('height')) {
			const ratio = this.image.width / this.image.height;
			this.workspace.height = this.workspace.width / ratio;
		} else {
			this.workspace.height = parseInt(this.$targetCanvas.getAttribute('height')!, 10);
		}

		const widthWithPadding = this.workspace.width;
		const heightWithPadding = this.workspace.height;

		this.workspace.widthFactor = (this.workspace.width / this.image.width);
		this.workspace.heightFactor = (this.workspace.height / this.image.height);

		this.$targetCanvas.setAttribute('width', widthWithPadding.toString());
		this.$targetCanvas.setAttribute('height', heightWithPadding.toString());

		if (this.$magnifier) {
			this.$magnifier.setAttribute('width', this.magSZ.toString());
			this.$magnifier.setAttribute('height', this.magSZ.toString());
		}
	}

	private setSelectionAreaValues = (pts: Array<Array<number>>, areOriginalCoords: boolean) => {
		if (pts) {
			this.selectionArea = new SelectionArea({
				pts,
				areOriginalCoords,
				options: this.options,
				workspace: this.workspace,
				targetCanvas: this.$targetCanvas
			});
		} else {
			this.selectionArea = new SelectionArea({
				pts: this.initSelectAreaPoints(this.workspace.width, this.workspace.height),
				areOriginalCoords: false,
				options: this.options,
				workspace: this.workspace,
				targetCanvas: this.$targetCanvas
			});
		}
	}

	private onTouchStart = (e: TouchEvent) => {
		if (this.pointerDownFunc(e)) {
			e.preventDefault();

			this.isInTouchMode = true;

			this.$targetCanvas.addEventListener('touchmove', this.onMove, { passive: true });
			this.$targetCanvas.addEventListener('touchend', this.onTouchEnd, { passive: true });
			this.$targetCanvas.addEventListener('touchcancel', this.onTouchEnd, { passive: true });
		}
	}

	private onTouchEnd = (e: TouchEvent) => {
		this.pointerUpFunc(e);

		this.$targetCanvas.removeEventListener('touchmove', this.onMove);
		this.$targetCanvas.removeEventListener('touchend', this.onTouchEnd);
		this.$targetCanvas.removeEventListener('touchcancel', this.onTouchEnd);
	}

	private onMouseDown = (e: MouseEvent) => {
		if (this.pointerDownFunc(e)) {
			e.preventDefault();

			this.isInTouchMode = false;

			this.$targetCanvas.addEventListener('mousemove', this.onMove, { passive: true });
			this.$targetCanvas.addEventListener('mouseup', this.onMouseUp, { passive: true });
			this.$targetCanvas.addEventListener('mouseout', this.onMouseUp, { passive: true });
		}
	}

	private onMouseUp = (e: MouseEvent) => {
		this.pointerUpFunc(e);

		this.$targetCanvas.removeEventListener('mousemove', this.onMove);
		this.$targetCanvas.removeEventListener('mouseup', this.onMouseUp);
		this.$targetCanvas.removeEventListener('mouseout', this.onMouseUp);
	}

	private onMove = (e: TouchEvent | MouseEvent) => {
		const target = e.target as HTMLCanvasElement;
		const { clientX, clientY } = this.getPointerLocation(e);
		const { x, y } = this.getRelativeTouchPosition(target, clientX, clientY);

		return this.moveCubes(Croppy.normalize(x, 0, target.width), Croppy.normalize(y, 0, target.height));
	}

	private getRelativeTouchPosition = (target: HTMLCanvasElement, clientX: number, clientY: number) => {
		let containerRect = target.getClientRects()[0];
		if (containerRect == undefined) {
			containerRect = new DOMRect();
		}
		const rect = this.getTargetActualSize(true, containerRect.width, containerRect.height, target.width, target.height);
		const scale = this.getTargetScale();

		return {
			x: (clientX - containerRect.left - rect.x) * scale.scaleX,
			y: (clientY - containerRect.top - rect.y) * scale.scaleY
		};
	}

	private setEvents = () => {
		this.$targetCanvas.addEventListener('touchstart', this.onTouchStart);
		this.$targetCanvas.addEventListener('mousedown', this.onMouseDown);

		// Watch to see if the canvas is resized and repaint if it is
		if (typeof (window as any).ResizeObserver === 'function') {
			new (window as any).ResizeObserver(() => this.drawScene())
				.observe(this.$targetCanvas);
		}
	}

	private getPointerLocation = (e: TouchEvent | MouseEvent) => {
		if (e instanceof TouchEvent) {
			const { clientX, clientY } = e.touches[0];
			return { clientX, clientY };
		} else if (e instanceof MouseEvent) {
			const { clientX, clientY } = e;
			return { clientX, clientY };
		} else {
			return { clientX: 0, clientY: 0 };
		}
	}

	private pointerDownFunc = (e: TouchEvent | MouseEvent) => {
		const { clientX, clientY } = this.getPointerLocation(e);
		const target = e.target as HTMLCanvasElement;
		const { x, y } = this.getRelativeTouchPosition(target, clientX, clientY);
		const pointer = this.getPointUnderCursor(x, y);

		if (pointer) {
			this.selectionArea.hovering[pointer.index] = true;
			this.selectionArea.dragging[pointer.index] = true;
			this.mouseDragging = true;
			this.drawScene();

			return true;
		} else {
			return false;
		}
	}

	private pointerUpFunc = (e: TouchEvent | MouseEvent) => {
		// Set all the drag statuses to false
		for (let i = 0; i < this.selectionArea.points.length; i++) {
			this.mouseDragging = false;
			this.selectionArea.dragging[i] = false;
			this.selectionArea.hovering[i] = false;
		}

		// Clear the magnified view
		if (this.magnifierContext) {
			this.magnifierContext.clearRect(0, 0, this.magSZ, this.magSZ);
		}

		this.drawScene();
	}

	private getPointUnderCursor = (xPos: number, yPos: number): { index: number; x: number; y: number; } | undefined => {
		// Calculate the position of the mouse on the canvas
		const mouseX = Croppy.normalize(Math.floor(xPos), 0, this.workspace.width);
		const mouseY = Croppy.normalize(Math.floor(yPos), 0, this.workspace.height);

		// Hovering over resize cubes
		if (this.selectionArea) {
			const scale = this.getTargetScale();

			for (let i = 0; i < this.selectionArea.points.length; i++) {
				const x = this.selectionArea.points[i][0];
				const y = this.selectionArea.points[i][1];

				const sx = this.selectionArea.cSizeH * scale.scaleX;
				const sy = this.selectionArea.cSizeH * scale.scaleY;

				if (Croppy.between(mouseX, (x - sx), (x + sx)) && Croppy.between(mouseY, (y - sy), (y + sy))) {
					return { index: i, x, y };
				}
			}
		}

		return;
	}

	private moveCubes = (x: number, y: number) => {
		const draggingPointerIndex = this.selectionArea.dragging.indexOf(true);

		// Hovering over resize cubes
		if (draggingPointerIndex >= 0) {
			if (this.selectionArea.hovering[draggingPointerIndex]) {
				this.selectionArea.dragging[draggingPointerIndex] = this.selectionArea.hovering[draggingPointerIndex];
			}

			if (this.selectionArea.dragging[draggingPointerIndex]) {
				this.selectionArea.points[draggingPointerIndex][0] = x;
				this.selectionArea.points[draggingPointerIndex][1] = y;
			}

			this.drawScene();
		}

	}

	private setCursor = (isDragging: boolean) => {
		if (isDragging) {
			this.$targetCanvas.style.cursor = this.cursorDraggingIcon;
		} else {
			this.$targetCanvas.style.cursor = this.cursorIcon;
		}
	}

	private drawPath = (context: CanvasRenderingContext2D) => {
		context.beginPath();

		for (let i = 0; i < this.selectionArea.points.length; i++) {
			const x = parseInt(String(this.selectionArea.points[i][0]).trim(), 10);
			const y = parseInt(String(this.selectionArea.points[i][1]).trim(), 10);

			if (i === 0) {
				context.moveTo(x, y);
			} else {
				context.lineTo(x, y);
			}
		}

		context.closePath();
	}

	private rotateCanvas = (context: CanvasRenderingContext2D) => {

		const canvas = context.canvas;

		context.clearRect(0, 0, canvas.width, canvas.height);

		// save the un-rotated context of the canvas so we can restore it later
		// the alternative is to un-translate & un-rotate after drawing
		context.save();

		const centerX = canvas.width / 2;
		const centerY = canvas.height / 2;

		// move to the center of the canvas
		context.translate(centerX, centerY);

		// rotate the canvas to the specified degrees
		context.rotate(this.rotationDegrees * Math.PI / 180);

		// draw the image
		// since the context is rotated, the image will be rotated also
		context.drawImage(this.image!, -centerX, -centerY, this.workspace.width, this.workspace.height);

		// we're done with the rotating so restore the un-rotated context
		context.restore();
	}

	private initSelectAreaPoints = (width: number, height: number) => {
		const x1 = 0;
		const y1 = 0;
		const x2 = width;
		const y2 = height;

		return [[x1, y1], [x1, y2], [x2, y2], [x2, y1]];
	}

	private drawSelectionArea = () => {

		if (!this.selectionArea) {
			return;
		}

		this.setDynamicLineWidth();

		const scale = this.getTargetScale();

		const sx = this.selectionArea.cSize * scale.scaleX;
		const sy = this.selectionArea.cSize * scale.scaleY;

		let drawn = false;
		let draggedCubeFound = !this.mouseDragging;

		// Draw grip cubes
		for (let i = 0; i < this.selectionArea.points.length; i++) {
			this.targetCanvasContext.fillStyle =
				this.selectionArea.dragging[i] ? this.selectionArea.highlight : this.selectionArea.textColor;

			const x = this.selectionArea.points[i][0];
			const y = this.selectionArea.points[i][1];

			if (this.selectionArea.dragging[i]) {

				draggedCubeFound = true;

				this.drawMagnifier(x, y);

				if (!drawn) {
					this.drawLineBetweenCubes();
					drawn = true;
				}

				this.targetCanvasContext.strokeStyle = this.selectionArea.highlight;

				if (this.isInTouchMode) {
					this.targetCanvasContext.beginPath();
					this.targetCanvasContext.ellipse(x, y, (sx * 3), (sy * 3), 0, 0 * Math.PI, 2 * Math.PI);
					this.targetCanvasContext.closePath();

					this.targetCanvasContext.stroke();
				} else {
					this.targetCanvasContext.strokeRect(x - ((sx * 2) / 2), y - ((sy * 2) / 2), (sx * 2), (sy * 2));
				}

				// First draw the grip cube
				this.targetCanvasContext.strokeStyle = this.selectionArea.lineColor;
			} else {
				if (draggedCubeFound && !drawn) {
					this.drawLineBetweenCubes();
					drawn = true;
				}

				// Draw filled cubes for the grips, larger if hovered over
				if (this.selectionArea.hovering[i]) {
					this.targetCanvasContext.fillRect(x - ((sx * 2) / 2), y - ((sy * 2) / 2), (sx * 2), (sy * 2));
				} else {
					this.targetCanvasContext.fillRect(x - (sx / 2), y - (sy / 2), sx, sy);
				}
			}

			// drawCoordinatesText(i);
		}
	}

	private drawMagnifier = (x: number, y: number) => {
		if (this.$magnifier && this.magnifierContext) {
			// Next we'll draw a magnified portion of the part of the
			// image under the cursor, with cross-hairs
			this.magnifierContext.clearRect(0, 0, this.magSZ, this.magSZ);

			const xPos = x < this.off ? 0 : x - this.off;
			const yPos = y < this.off ? 0 : y - this.off;
			const fromRight = this.$targetCanvas.width - x;
			const fromBottom = this.$targetCanvas.height - y;
			const wid = fromRight <= this.off ? this.off + fromRight - 1 : this.off * 2;
			const hgt = fromBottom <= this.off ? this.off + fromBottom - 1 : this.off * 2;
			const fac = 1 / (this.off * 2);

			this.magnifierContext.drawImage(this.$targetCanvas, xPos, yPos, wid, hgt, (xPos - (x - this.off)) * this.zs / 2, (yPos - (y - this.off)) * this.zs / 2, Math.floor(wid * fac * this.magSZ), Math.floor(hgt * fac * this.magSZ));

			// Vertical
			this.magnifierContext.beginPath();
			this.magnifierContext.moveTo(this.magSZ / 2, 0);
			this.magnifierContext.lineTo(this.magSZ / 2, this.magSZ);
			this.magnifierContext.closePath();
			this.magnifierContext.stroke();

			// Horizontal

			this.magnifierContext.beginPath();
			this.magnifierContext.moveTo(0, this.magSZ / 2);
			this.magnifierContext.lineTo(this.magSZ, this.magSZ / 2);
			this.magnifierContext.closePath();
			this.magnifierContext.stroke();
		}
	}

	private drawLineBetweenCubes = () => {
		// Draw lines between the corners
		this.targetCanvasContext.strokeStyle = this.selectionArea.lineColor;

		this.setDynamicLineWidth();

		this.targetCanvasContext.beginPath();
		this.targetCanvasContext.moveTo(this.selectionArea.points[0][0], this.selectionArea.points[0][1]);
		for (let j = 1; j < this.selectionArea.points.length; j++) {
			this.targetCanvasContext.lineTo(this.selectionArea.points[j][0], this.selectionArea.points[j][1]);
		}
		this.targetCanvasContext.lineTo(this.selectionArea.points[0][0], this.selectionArea.points[0][1]);
		this.targetCanvasContext.closePath();
		this.targetCanvasContext.stroke();
	}

	private drawScene = (noSquares: boolean = false) => {
		this.rotateCanvas(this.targetCanvasContext);

		if (!noSquares) {
			this.drawSelectionArea();
		}
	}

	private getTargetActualSize = (contains: boolean, containerWidth: number, containerHeight: number, width: number, height: number) => {
		const doRatio = width / height;
		const cRatio = containerWidth / containerHeight;
		let targetWidth = 0;
		let targetHeight = 0;
		const test = contains ? (doRatio > cRatio) : (doRatio < cRatio);

		if (test) {
			targetWidth = containerWidth;
			targetHeight = targetWidth / doRatio;
		} else {
			targetHeight = containerHeight;
			targetWidth = targetHeight * doRatio;
		}

		return {
			height: targetHeight,
			width: targetWidth,
			x: (containerWidth - targetWidth) / 2,
			y: (containerHeight - targetHeight) / 2,
		};
	}

	private getTargetScale = () => {
		let containerRect = this.$targetCanvas.getClientRects()[0];
		if (containerRect == undefined) {
			containerRect = new DOMRect();
		}
		const rect = this.getTargetActualSize(
			true,
			containerRect.width,
			containerRect.height,
			this.$targetCanvas.width,
			this.$targetCanvas.height,
		);

		if (rect) {
			return {
				scaleX: this.$targetCanvas.width / rect.width,
				scaleY: this.$targetCanvas.height / rect.height,
			};
		} else {
			return {
				scaleX: 1,
				scaleY: 1,
			};
		}
	}

	private setDynamicLineWidth = () => {
		const scale = this.getTargetScale();

		this.targetCanvasContext.lineWidth = 2 * ((scale.scaleX + scale.scaleY) / 2);
	}

}

class SelectionArea {
	public points: Array<Array<number>>;
	public highlight: string;
	public lineColor: string;
	public textColor: string;
	public hovering: [boolean, boolean, boolean, boolean] = [false, false, false, false];
	public dragging: [boolean, boolean, boolean, boolean] = [false, false, false, false];

	public cSize: number = 10;
	public cSizeH: number = 20;

	private initCanvasLeft: number;
	private initCanvasTop: number;

	constructor({ pts,
		areOriginalCoords,
		options,
		workspace,
		targetCanvas
	}: {
		pts: Array<Array<number>>;
		areOriginalCoords: boolean;
		options: ICroppyOptions;
		workspace: IWorkspace;
		targetCanvas: HTMLCanvasElement;
	}) {
		if (areOriginalCoords) {
			for (let i = 0; i < pts.length; i++) {
				pts[i][0] = Croppy.getFloat(pts[i][0] * workspace.widthFactor);
				pts[i][0] = pts[i][0] < workspace.width
					? pts[i][0] > 0 ? pts[i][0] : 0
					: workspace.width;

				pts[i][1] = Croppy.getFloat(pts[i][1] * workspace.heightFactor);
				pts[i][1] = pts[i][1] < workspace.height
					? pts[i][1] > 0 ? pts[i][1] : 0
					: workspace.height;
			}
		}

		this.points = pts;

		this.highlight = options.selection.highlightColor;
		this.lineColor = options.selection.lineColor;
		this.textColor = options.selection.coordsTextColor;

		const canvasOffset = Croppy.getOffset(targetCanvas);

		this.initCanvasLeft = canvasOffset.left;
		this.initCanvasTop = canvasOffset.top;
	}

}
